/**
 * Provider Configuration Registry
 *
 * Maps integration provider IDs to their detail panel components.
 * When a user clicks "Configure" on any integration in the grid view,
 * the registry resolves the appropriate panel component.
 *
 * To add a new provider:
 *   1. Create a <ProviderName>Config.tsx in this directory
 *   2. Register it in the providerRegistry below
 *   3. The IntegrationsConfig grid will automatically route to it
 */

import type { ComponentType } from 'react';

export interface ProviderConfigPanelProps {
  /** Integration record from the API */
  integrationId: string;
  /** Callback to return to the grid view */
  onBack: () => void;
}

/** Registry entry for a provider configuration panel */
export interface ProviderRegistryEntry {
  /** Display name shown in panel header */
  displayName: string;
  /** Provider-specific config panel component */
  component: ComponentType<ProviderConfigPanelProps>;
  /** Short description of the integration */
  description: string;
}

/**
 * Lazy-loaded provider registry.
 *
 * Components are imported dynamically to avoid bundling all provider
 * panels into the main chunk. The registry key matches the integration
 * record's `providerId` or `name` field from the API.
 */
const providerRegistry: Record<string, () => Promise<ProviderRegistryEntry>> = {
  'cuanswers': async () => {
    const { CUAnswersConfigPanel } = await import('./CUAnswersConfig');
    return {
      displayName: 'CU*Answers',
      component: CUAnswersConfigPanel,
      // This string is the first thing an operator reads about the integration,
      // in the provider list before the panel opens. It read 'CU*BASE core
      // banking integration via OLB and CSI APIs' until 2026-09-10. Both names
      // are wrong and both send an operator looking for credentials that do not
      // exist: 'CSI' is an Aaim coinage with zero occurrences in either vendor
      // OpenAPI document (the vendor calls it the Integrations API), and there
      // is no OLB surface at all -- olb.cuanswers.com and olb-qa.cuanswers.com
      // both return NXDOMAIN.
      description: 'CU*Answers Integrations API -- membership tracker creation over HTTP Basic',
    };
  },
};

/**
 * Resolve a provider's configuration panel by ID.
 * Returns null if no panel is registered for this provider.
 */
export async function resolveProviderPanel(
  providerId: string,
): Promise<ProviderRegistryEntry | null> {
  const normalizedId = providerId.toLowerCase().replace(/[^a-z0-9]/g, '');
  const loader = providerRegistry[normalizedId];
  if (!loader) return null;
  return loader();
}

/**
 * Check if a provider has a registered configuration panel.
 *
 * Keyed on the catalog row's display name, which is what the grid passes. The
 * one registered panel, CU*Answers, is reachable from no catalog row: the only
 * CU*Answers row is 'CU*Answers SSO' ('cuanswerssso'), a different integration
 * from the Integrations API this panel configures, and which row the panel
 * belongs to is an open operator question. Integration declares no stable
 * provider key (platform-admin.openapi.yaml Integration), so the registry has
 * nothing better than a display string to key on until one is declared.
 */
export function hasProviderPanel(providerId: string): boolean {
  const normalizedId = providerId.toLowerCase().replace(/[^a-z0-9]/g, '');
  return normalizedId in providerRegistry;
}

/** A provider whose configuration lives on another page of this console. */
export interface ProviderLinkConfig {
  /** The console route that configures the provider. */
  href: string;
  /** The link text: names the page it opens. */
  label: string;
}

/**
 * Providers configured on a page of their own rather than in a panel of the
 * integrations grid. ATTOM's API key is set on the Configuration tab of
 * ATTOM Property Data (/integrations/attom, pages/AttomTestInterface.tsx).
 *
 * Matched against the row's vendor and then its name, both display strings,
 * for the reason hasProviderPanel gives. The seeded row is name 'ATTOM Data',
 * vendor 'ATTOM Data Solutions' (platform-admin-api seed.ts).
 */
const PROVIDER_LINKS: ReadonlyArray<{ keys: readonly string[]; link: ProviderLinkConfig }> = [
  {
    keys: ['attomdatasolutions', 'attomdata'],
    link: { href: '/integrations/attom', label: 'Configure on ATTOM Property Data' },
  },
];

function normalizeProviderKey(value: string | undefined): string {
  return (value ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

/**
 * The console page that configures a catalog row, or null when no page of
 * this console does.
 */
export function resolveProviderLink(row: { name: string; vendor?: string }): ProviderLinkConfig | null {
  const candidates = [normalizeProviderKey(row.vendor), normalizeProviderKey(row.name)].filter(Boolean);
  for (const entry of PROVIDER_LINKS) {
    if (candidates.some((c) => entry.keys.includes(c))) return entry.link;
  }
  return null;
}
